import React, { useState, useEffect } from 'react';

/**
 * Cookie Consent Banner Component
 * 
 * This component handles:
 * 1. Displaying a cookie consent banner to first-time visitors
 * 2. Storing user consent preference in localStorage
 * 3. Pushing consent events to GTM dataLayer
 * 4. GTM handles GA4 initialization via consent mode
 * 
 * GDPR Compliance:
 * - Analytics only enabled AFTER explicit consent via GTM consent mode
 * - User choice is remembered and respected
 * - Easy opt-out via browser settings or declining
 * 
 * FLOW:
 * 1. GTM loads with default consent denied (via Consent Mode tag)
 * 2. This component checks localStorage for previous consent
 * 3. If accepted previously: pushes consent_update event to GTM
 * 4. If no choice: shows banner
 * 5. On accept/decline: pushes cookie_consent event to GTM
 * 6. GTM updates consent state and enables/disables GA4 accordingly
 */

// Your actual Google Analytics 4 Measurement ID (used for reference)
const GA_MEASUREMENT_ID = 'G-4BHY59N7N1';

// Check if consent was given
export const hasAnalyticsConsent = () => {
  return localStorage.getItem('cookieConsent') === 'accepted';
};

// Get consent status
export const getConsentStatus = () => {
  return localStorage.getItem('cookieConsent'); // 'accepted', 'declined', or null
};

function CookieConsent() {
  const [showBanner, setShowBanner] = useState(false);

  useEffect(() => {
    // Ensure dataLayer exists
    window.dataLayer = window.dataLayer || [];
    
    // Check if user has already made a choice
    const consent = localStorage.getItem('cookieConsent');
    
    if (consent === null) {
      // No choice made yet, show banner
      setShowBanner(true);
      
      // Track that consent banner was shown
      window.dataLayer.push({
        event: 'consent_banner_shown',
        consent_status: 'pending'
      });
    } else if (consent === 'accepted') {
      // User previously accepted - notify GTM to update consent

      updateGTMConsent('granted');
    }
    // If consent === 'declined', GTM consent remains denied (default state)
  }, []);

  /**
   * Update GTM consent state
   * This triggers GTM's consent mode to update, which then enables/disables GA4
   */
  const updateGTMConsent = (state) => {
    window.dataLayer = window.dataLayer || [];
    
    // Push consent update for GTM consent mode
    window.dataLayer.push({
      event: 'consent_update',
      analytics_consent: state,
      ad_consent: state
    });
    
    // Also define gtag consent update if gtag is available
    if (typeof window.gtag === 'function') {
      window.gtag('consent', 'update', {
        'analytics_storage': state,
        'ad_storage': state
      });
    }
  };

  const acceptCookies = () => {
    const consentDate = new Date().toISOString();
    localStorage.setItem('cookieConsent', 'accepted');
    localStorage.setItem('cookieConsentDate', consentDate);
    
    // Push consent acceptance event to GTM
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: 'cookie_consent',
      consent_action: 'accepted',
      consent_date: consentDate
    });
    
    // Update GTM consent state to allow analytics
    updateGTMConsent('granted');

    setShowBanner(false);
    
    console.log('Cookie consent accepted - GTM will enable analytics');
  };

  const declineCookies = () => {
    const consentDate = new Date().toISOString();
    localStorage.setItem('cookieConsent', 'declined');
    localStorage.setItem('cookieConsentDate', consentDate);
    
    // Push consent decline event to GTM
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: 'cookie_consent',
      consent_action: 'declined',
      consent_date: consentDate
    });
    
    // Ensure GTM consent state remains denied
    updateGTMConsent('denied');
    
    setShowBanner(false);
    
    console.log('Cookie consent declined - analytics remain disabled');
  };

  if (!showBanner) return null;

  return (
    <div className="fixed bottom-0 left-0 right-0 z-[200] p-4 bg-white border-t border-gray-200 shadow-2xl">
      <div className="max-w-7xl mx-auto">
        <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-2">
              <svg className="w-6 h-6 text-blue-900" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
              </svg>
              <h3 className="text-lg font-semibold text-blue-900">Cookie Notice</h3>
            </div>
            <p className="text-gray-600 text-sm md:text-base">
              We use cookies to analyze website traffic and improve your experience. 
              By clicking "Accept", you consent to our use of analytics cookies.{' '}
              <a href="/cookie-policy" className="text-red-600 hover:underline font-medium">
                Learn more
              </a>
            </p>
          </div>
          <div className="flex flex-col sm:flex-row gap-3 w-full md:w-auto">
            <button
              onClick={declineCookies}
              className="px-6 py-2.5 border-2 border-gray-300 text-gray-700 hover:bg-gray-100 font-medium rounded-lg transition-colors text-sm"
            >
              Decline
            </button>
            <button
              onClick={acceptCookies}
              className="px-6 py-2.5 bg-blue-900 hover:bg-red-600 text-white font-medium rounded-lg transition-colors text-sm"
            >
              Accept Cookies
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * Helper function to track custom events
 * Events will only be recorded if consent was given (GTM handles this via consent mode)
 */
export const trackEvent = (eventName, eventParams = {}) => {
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({
    event: eventName,
    ...eventParams
  });
  
  // Also try gtag if available
  if (window.gtag && hasAnalyticsConsent()) {
    window.gtag('event', eventName, eventParams);
  }
  
  console.log(`Analytics Event: ${eventName}`, eventParams);
  return hasAnalyticsConsent();
};

/**
 * Helper function to track page views manually (useful for SPA navigation)
 */
export const trackPageView = (pagePath, pageTitle) => {
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({
    event: 'page_view',
    page_path: pagePath,
    page_title: pageTitle
  });
  
  if (window.gtag && hasAnalyticsConsent()) {
    window.gtag('config', GA_MEASUREMENT_ID, {
      page_path: pagePath,
      page_title: pageTitle
    });
  }
  
  console.log(`Analytics Page View: ${pagePath}`);
  return hasAnalyticsConsent();
};

// Export GA Measurement ID for use in other files
export { GA_MEASUREMENT_ID };

export default CookieConsent;